// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createCashRegister,
  deleteCashRegister,
  getCashRegister,
  getCashRegisters,
  selectCashRegisters,
  selectFinancialSettingsIsLoading,
  updateCashRegister,
} from '../../feature';

// Components
import { Modal, ModalSize, showSuccessToast } from '@/components';
import { PlusCircleIcon } from '@/icons';
import { Button, Drawer } from '@/ui';
import { CashRegisterForm, CashRegisterList } from '../../components';
import {
  StyledActionButtons,
  StyledContentBlock,
  StyledCreateButton,
  StyledTabTitleWrapper,
} from '../../components/Styled';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { ICashRegister } from '@/models/financialSettings.model';
import { ROUTES } from '@/models/router.model';

// Helpers
import { usePagination } from '@/hooks';
import { getInitialCashRegisterValues } from '../../helpers/getInitialValues';
import { TCashRegisterFormValues } from '../../helpers/types';

const PAGE_BASE = ROUTES.ACCOUNTING_CASH_REGISTERS_INDEX;

const CashRegistersContainer = () => {
  const { t } = useTranslation(['financialSettings']);
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const [cashRegister, setCashRegister] = useState<ICashRegister | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { currentPage, onPageChange } = usePagination();

  const dispatch = useAppDispatch();
  const cashRegisters = useAppState(selectCashRegisters);
  const isLoading = useAppState(selectFinancialSettingsIsLoading);
  const { canManage } = usePermissions();

  const canManageCashRegister = canManage(Resource.CASH_REGISTER);
  const isEditMode = Boolean(id && id !== 'create');

  const getCashRegistersListHandler = () => {
    dispatch(getCashRegisters({ page: currentPage }));
  };

  const onDrawerClose = () => {
    setCashRegister(null);
    navigate(PAGE_BASE);
  };

  const handleOpenDrawer = async (item?: ICashRegister) => {
    try {
      if (item?.id) {
        const result = await dispatch(getCashRegister(item.id)).unwrap();
        setCashRegister(result.data);
      }

      return navigate(`${PAGE_BASE}/${item?.id ?? 'create'}`);
    } catch (error) {}
  };

  const handleSubmit = async (values: TCashRegisterFormValues) => {
    try {
      const isEdit = !!cashRegister?.id;

      if (isEdit) {
        await dispatch(updateCashRegister({ ...values, id: cashRegister.id })).unwrap();
      } else {
        await dispatch(createCashRegister(values)).unwrap();
      }

      showSuccessToast({
        message: t(isEdit ? 'cash_register_edit_success' : 'cash_register_create_success'),
      });
      onDrawerClose();
      getCashRegistersListHandler();
    } catch (error) {}
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    try {
      await dispatch(deleteCashRegister(deleteId)).unwrap();
      showSuccessToast({ message: t('cash_register_delete_success') });
      setDeleteId(null);
      getCashRegistersListHandler();
    } catch (error) {}
  };

  const handleDelete = (item: ICashRegister) => setDeleteId(item.id);

  const handleCloseDeleteModal = () => setDeleteId(null);

  useEffect(() => {
    getCashRegistersListHandler();
  }, [currentPage]);

  return (
    <>
      <StyledTabTitleWrapper>
        <span>{t('cash_registers_subtitle')}</span>
        {canManageCashRegister && (
          <StyledCreateButton onClick={() => handleOpenDrawer()} type="primary">
            <PlusCircleIcon />
            {t('cash_register_add')}
          </StyledCreateButton>
        )}
      </StyledTabTitleWrapper>
      <StyledContentBlock>
        <CashRegisterList
          cashRegisters={cashRegisters?.data ?? []}
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onDelete={handleDelete}
          onUpdate={handleOpenDrawer}
          total={cashRegisters?.meta?.total ?? 0}
        />
        <Drawer
          open={!!id}
          onClose={onDrawerClose}
          width="29rem"
          destroyOnClose
          title={t(isEditMode ? 'cash_register_edit' : 'cash_register_add')}
        >
          <CashRegisterForm
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isEdit={isEditMode}
            initialValues={getInitialCashRegisterValues(cashRegister)}
          />
        </Drawer>
        <Modal
          visible={!!deleteId}
          onCancel={handleCloseDeleteModal}
          size={ModalSize.MEDIUM}
          title={t('cash_register_delete_title')}
          withFooter
          footer={
            <StyledActionButtons>
              <Button block onClick={handleCloseDeleteModal} type="primary">
                {t('button_common_back', { ns: 'common' })}
              </Button>
              <Button block onClick={handleConfirmDelete} type="text">
                {t('button_common_delete', { ns: 'common' })}
              </Button>
            </StyledActionButtons>
          }
        />
      </StyledContentBlock>
    </>
  );
};

export default CashRegistersContainer;
